import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ListHeading, ListSubheading, Tree, Flex, Badge, Icon, Spinner } from '@oliasoft-open-source/react-ui-library';
import {
  taskSelected,
  reorderTasks,
  tasksDeselected,
  addInitialTask,
  openTaskIdsUpdated,
} from 'store/entities/activity-model/activity-model';
import translations from 'src/internationalisation/translation-map.json';
import { selectExtendedLinkFields, selectOpenTaskIds } from 'store/entities/activity-model/selectors';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';

const TasksList = ({
  tasks,
  activeOperation,
  setActiveForm,
  taskSelected,
  reorderTasks,
  tasksDeselected,
  onClickDeleteTask,
  addInitialTask,
  isAdding,
  treeRef,
  expandAllItems,
  collapseAllItems,
  openNode,
  isPageDisabled,
  projectId,
  linkedFields,
  rigOperationTemplates,
  handleTaskImportModal,
  setIsLinkedTaskImport,
  setActivityParentId,
  openTaskIds,
  openTaskIdsUpdated,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const onClickAdd = () => {
    tasksDeselected();
    addInitialTask(
      activeOperation.operationId,
      projectId,
      t(translations.activityModel_newActivity),
      null,
      linkedFields,
    );
    setActiveForm('task');
  };

  const onClickItem = (index) => {
    taskSelected(index);
    setActiveForm('task');
  };

  const onClickAddRisk = (id) => {
    tasksDeselected();
    addInitialTask(activeOperation.operationId, projectId, t(translations.activityModel_newRisk), id, linkedFields);
    setActiveForm('task');
    openNode(id);
  };
  const onClickExpandCollapse = () => {
    if (expanded) {
      collapseAllItems();
    } else {
      expandAllItems();
    }
    setExpanded(!expanded);
  };

  const getRigOperationTemplateName = (task) => {
    const rigTemplate = rigOperationTemplates.find((template) =>
      template.activities?.some(
        (templateActivity) => templateActivity.rigOperationTemplateActivityId === task.rigTemplateActivityId,
      ),
    );

    return rigTemplate ? rigTemplate.name : '';
  };

  const tasksList = activeOperation?.tasks
    ? activeOperation.tasks
        .map((task, i) =>
          task.isBranch
            ? null
            : {
                id: task.taskId,
                name: task.parentId ? (
                  <Flex gap="var(--padding-xs)" alignItems="center" wrap={false}>
                    <Badge title="RISK" small />
                    {task.name}
                    {task.withBranch ? <Icon icon="fork" /> : null}
                  </Flex>
                ) : (
                  task.name
                ),
                testId: `activities-list-${convertToKebabCase(task.name)}-item`,
                droppable: true,
                active: task.active,
                parent: task.parentId || 0,
                onClick: () => onClickItem(i),
                actions: [
                  ...((!!task?.rigTemplateActivityId || !!task?.isImported) && !task?.isLinked
                    ? [
                        {
                          icon: 'download',
                        },
                      ]
                    : []),
                  ...(!!task?.rigTemplateActivityId && task?.isLinked
                    ? [
                        {
                          icon: 'link',
                          tooltip: `Rig Operation Template: ${getRigOperationTemplateName(task)}`,
                        },
                      ]
                    : []),
                  {
                    testId: `activities-list-${convertToKebabCase(task.name)}-more-button`,
                    label: t(translations.more),
                    subActions: [
                      {
                        testId: `activities-list-${convertToKebabCase(task.name)}-delete-button`,
                        label: t(translations.delete),
                        icon: 'delete',
                        onClick: () => onClickDeleteTask(task),
                        disabled: isAdding || isPageDisabled,
                      },
                      ...(task.withBranch
                        ? []
                        : [
                            {
                              label: t(translations.activityModel_addRisk),
                              icon: 'add',
                              onClick: () => onClickAddRisk(task.taskId),
                              disabled: isAdding || isPageDisabled,
                            },
                            {
                              label: t(translations.activityModel_importFromTemplate),
                              onClick: () => {
                                setIsLinkedTaskImport(false);
                                handleTaskImportModal(true);
                                setActivityParentId(task.taskId);
                              },
                              disabled: isAdding || isPageDisabled,
                              icon: 'download',
                            },
                            {
                              label: t(translations.activityModel_importAndLinkToTemplate),
                              onClick: () => {
                                setIsLinkedTaskImport(true);
                                handleTaskImportModal(true);
                                setActivityParentId(task.taskId);
                              },
                              disabled: isAdding || isPageDisabled,
                              icon: 'link',
                            },
                          ]),
                    ],
                  },
                ],
              },
        )
        .filter((item) => item)
    : [];

  return (
    <>
      <ListHeading name={activeOperation?.name || t(translations.activityModel_newOperation)} />
      <ListSubheading
        item={{
          name: `${t(translations.activities)} (${tasksList.length})`,
          actions: [
            {
              label: 'Expand/collapse',
              icon: expanded ? 'collapse all' : 'expand all',
              onClick: onClickExpandCollapse,
              disabled: isAdding || isPageDisabled,
            },
            {
              label: t(translations.more),
              icon: 'download',
              subActions: [
                {
                  label: t(translations.activityModel_importFromTemplate),
                  onClick: () => {
                    setIsLinkedTaskImport(false);
                    handleTaskImportModal(true);
                    setActivityParentId(null);
                  },
                  disabled: isAdding || isPageDisabled,
                  icon: 'download',
                },
                {
                  label: t(translations.activityModel_importAndLinkToTemplate),
                  onClick: () => {
                    setIsLinkedTaskImport(true);
                    handleTaskImportModal(true);
                    setActivityParentId(null);
                  },
                  disabled: isAdding || isPageDisabled,
                  icon: 'link',
                },
              ],
            },
            {
              testId: 'activities-list-subheading-add-button',
              label: 'Add',
              icon: isAdding ? <Spinner tiny dark /> : 'add',
              primary: true,
              onClick: onClickAdd,
              disabled: isAdding || isPageDisabled,
            },
          ],
        }}
      />
      <Tree
        list={{
          items: tasksList,
        }}
        draggable={!isPageDisabled}
        noHeader
        onListReorder={(reordered) => reorderTasks(reordered, tasks)}
        onChangeOpen={openTaskIdsUpdated}
        isInitialOpen={openTaskIds}
        treeRef={treeRef}
        allowParentReassignment
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    companySettings: { rigOperationTemplates },
  } = state.entities;
  return {
    linkedFields: selectExtendedLinkFields(state),
    rigOperationTemplates,
    openTaskIds: selectOpenTaskIds(state),
  };
};

const mapDispatchToProps = {
  taskSelected,
  reorderTasks,
  tasksDeselected,
  addInitialTask,
  openTaskIdsUpdated,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(TasksList));

export { Container as TasksList };
